import { ReactNode } from "react";

/** Labels/help/titles can be ReactNode; placeholders must be string */
type LabelNode = ReactNode;

export interface IContractorCopy {
  appTitle: LabelNode;
  sectionHelpHeader: LabelNode;

  buttons: {
    previous: LabelNode;
    next: LabelNode;
    submit: LabelNode;
    wizardView: LabelNode;
    verticalView: LabelNode;
    demoToggle: LabelNode;
    completeBadge: LabelNode;
  };

  business: {
    title: LabelNode;
    help: LabelNode;
    fields: {
      legalName: { label: LabelNode; placeholder: string };
      dba: { label: LabelNode; placeholder: string };
      yearsInBusiness: { label: LabelNode; placeholder: string };
      primaryTrade: { label: LabelNode; placeholder: string };
      state: { label: LabelNode; placeholder: string };
      employees: { label: LabelNode; placeholder: string };
      annualRevenue: { label: LabelNode; placeholder: string };
    };
  };

  licHistory: {
    title: LabelNode;
    help: LabelNode;
    fields: {
      licenseNumber: { label: LabelNode; placeholder: string };
      licenseState: { label: LabelNode; placeholder: string };
      claimsLast3Yrs: { label: LabelNode; placeholder: string };
      safetyProgram: {
        label: LabelNode;
        placeholder: string;
        options: { yes: LabelNode; no: LabelNode };
      };
    };
  };

  workersComp: {
    title: LabelNode;
    help: LabelNode;
    needWcCheckbox: LabelNode;
    fields: {
      payroll: { label: LabelNode; placeholder: string };
      wcClassCode: { label: LabelNode; placeholder: string };
    };
  };

  commercialAuto: {
    title: LabelNode;
    help: LabelNode;
    hasVehiclesCheckbox: LabelNode;
    fields: {
      vehicleCount: { label: LabelNode; placeholder: string };
      radius: { label: LabelNode; placeholder: string };
      hasLaddersRacks: {
        label: LabelNode;
        placeholder: string;
        options: { yes: LabelNode; no: LabelNode };
      };
    };
  };

  coverage: {
    title: LabelNode;
    help: LabelNode;
    fields: {
      glLimit: {
        label: LabelNode;
        placeholder: string;
        options: { _1m2m: LabelNode; _2m2m: LabelNode; custom: LabelNode };
      };
      addtlInsureds: {
        label: LabelNode;
        placeholder: string;
        options: { yes: LabelNode; no: LabelNode };
      };
    };
  };

  contact: {
    title: LabelNode;
    help: LabelNode;
    fields: {
      firstName: { label: LabelNode; placeholder: string };
      lastName: { label: LabelNode; placeholder: string };
      email: { label: LabelNode; placeholder: string };
      phone: { label: LabelNode; placeholder: string };
    };
  };

  submitThankYou: {
    header: LabelNode;
    body: LabelNode;
  };
}

export const CONTRACTOR_COPY: IContractorCopy = {
  appTitle: <>🏗 Contractor Insurance Quote</>,
  sectionHelpHeader: <>Section Help</>,

  buttons: {
    previous: <>Previous</>,
    next: <>Next</>,
    submit: (
      <>
        🚀 <strong>Submit</strong>
      </>
    ),
    wizardView: <>Wizard View</>,
    verticalView: <>Vertical View</>,
    demoToggle: <>Demo Mode (auto-fill sample data)</>,
    completeBadge: <>Complete</>,
  },

  business: {
    title: <>Business Information</>,
    help: <>Tell us about your contracting business and operations.</>,
    fields: {
      legalName: { label: <>Legal Business Name</>, placeholder: "Acme Contracting LLC" },
      dba: { label: <>DBA (optional)</>, placeholder: "Acme Painting" },
      yearsInBusiness: { label: <>Years in Business</>, placeholder: "e.g. 5" },
      primaryTrade: { label: <>Primary Trade</>, placeholder: "Painting, Plumbing, Electrical…" },
      state: { label: <>Business State</>, placeholder: "Select a state…" },
      employees: { label: <>Number of Employees</>, placeholder: "e.g. 6" },
      annualRevenue: { label: <>Estimated Annual Revenue</>, placeholder: "e.g. 750000" },
    },
  },

  licHistory: {
    title: <>License & History</>,
    help: (
      <>
        Your license info and any recent claims help determine eligibility and rate.
      </>
    ),
    fields: {
      licenseNumber: { label: <>Contractor License #</>, placeholder: "ABC-123456" },
      licenseState: { label: <>License State</>, placeholder: "Select a state…" },
      claimsLast3Yrs: { label: <>Claims (last 3 yrs)</>, placeholder: "e.g. 0" },
      safetyProgram: {
        label: <>Formal Safety Program</>,
        placeholder: "Select…",
        options: { yes: <>Yes</>, no: <>No</> },
      },
    },
  },

  workersComp: {
    title: <>Workers’ Compensation</>,
    help: <>Enable if you need workers’ comp coverage; then add payroll details.</>,
    needWcCheckbox: <>I need Workers’ Comp</>,
    fields: {
      payroll: { label: <>Estimated Annual Payroll ($)</>, placeholder: "e.g. 420000" },
      wcClassCode: { label: <>WC Class Code</>, placeholder: "e.g. 5403 (Carpentry)" },
    },
  },

  commercialAuto: {
    title: <>Commercial Auto</>,
    help: <>Enable if you have company vehicles to insure.</>,
    hasVehiclesCheckbox: <>We have company vehicles</>,
    fields: {
      vehicleCount: { label: <>Vehicle Count</>, placeholder: "e.g. 3" },
      radius: { label: <>Typical Driving Radius (miles)</>, placeholder: "e.g. 50" },
      hasLaddersRacks: {
        label: <>Ladders / Racks Installed?</>,
        placeholder: "Select…",
        options: { yes: <>Yes</>, no: <>No</> },
      },
    },
  },

  coverage: {
    title: <>Coverage (Optional)</>,
    help: <>Pick a preferred GL limit and whether you often need Additional Insured certificates.</>,
    fields: {
      glLimit: {
        label: <>Preferred GL Limit</>,
        placeholder: "Select…",
        options: {
          _1m2m: <>$1M / $2M</>,
          _2m2m: <>$2M / $2M</>,
          custom: <>Custom</>,
        },
      },
      addtlInsureds: {
        label: <>Need Additional Insureds?</>,
        placeholder: "Select…",
        options: { yes: <>Yes</>, no: <>No</> },
      },
    },
  },

  contact: {
    title: <>Contact Information</>,
    help: <>How should we reach you with your quote?</>,
    fields: {
      firstName: { label: <>First Name</>, placeholder: "Jane" },
      lastName: { label: <>Last Name</>, placeholder: "Builder" },
      email: { label: <>Email</>, placeholder: "you@example.com" },
      phone: { label: <>Phone</>, placeholder: "(555) 555-1234" },
    },
  },

  submitThankYou: {
    header: <>Thank you!</>,
    body: (
      <>
        An agent will reach out to review your application. You can also{" "}
        <a href="/schedule">schedule a call</a>.
      </>
    ),
  },
};
